import cloneDeep from 'lodash/cloneDeep';
import shortid from 'shortid';
import {
	buildOptionSetAttachUpdates,
	buildOptionSetDetachAllUpdates,
	buildOptionSetDetachUpdates,
} from '@lib/common';
import { RootStore } from '../../../../../mobx/store';
import { UI } from '../../../../../core/ui';
import { MENU_CONFLICT_MESSAGE } from '../../../../../mobx/stores/menu_conflict';
import { logger } from "@lib/common";
import { hasOwnChannel } from "./utils/posChannel";

type RestaurantSchema = T.Schema.Restaurant.RestaurantSchema;
type RestaurantMenu = T.Schema.Restaurant.Menu.RestaurantMenu;
type RestaurantCategory = T.Schema.Restaurant.Menu.RestaurantCategory;
type RestaurantDish = T.Schema.Restaurant.Menu.RestaurantDish;
type RestaurantOptionSet = T.Schema.Restaurant.Menu.RestaurantOptionSet;
type RestaurantDishTag = T.Schema.Restaurant.Menu.RestaurantDishTag;

function mapTagsFromApi(tags: any[]): RestaurantDishTag[] {
	return tags.map((t) => ({
		_id: t.id ?? t._id,
		name: t.name ?? '',
		type: t.type ?? 'none',
		text: t.text ?? '',
		icon: t.icon ?? '',
		color: t.color ?? {
			icon: { background: '#fff', text: '#ff7d4b' },
			wrapper: { background: '#ff7d4b', text: '#fff' },
		},
	}));
}

export interface DishRefOriginal {
	menu_id: string;
	category_id: string;
	id: string;
}

type DishPatchItem = DishRefOriginal &
	Partial<Pick<RestaurantDish, 'option_sets' | 'tags' | 'status' | 'price'>>;

/**
 * Reports a save that did not go through.
 *
 * A menu conflict is a refusal, not a failure: the server declined on purpose
 * so another person's work is not overwritten, and the conflict dialog is
 * already explaining who changed it and what to do. Red on top of that reads
 * as something having gone wrong.
 */
export function notifyMenuSaveFailure(error: unknown): void {
	if ((error as { conflict?: string } | null)?.conflict === 'menu_version') {
		UI.notification.warning(MENU_CONFLICT_MESSAGE);
		return;
	}
	UI.notification.error(getMenuApiErrorMessage(error));
}

export function getMenuApiErrorMessage(error: unknown): string {
	if (!error || typeof error !== 'object') return 'An error occurred';
	const e = error as { error?: string; message?: string };
	return e.error ?? e.message ?? 'An error occurred';
}

/** Original schema persists menus on the restaurant doc, not via external menu-api. */
function usesLegacyMenuPersistence(store: RootStore): boolean {
	return !store.isNewMenuSchema;
}

async function persistRestaurantDoc(
	store: RootStore,
	updateSet: Record<string, unknown>,
	localUpdate: Partial<RestaurantSchema>
): Promise<void> {
	const r = store.restaurant!;
	const res = await store.api.restaurant_update({
		_id: r._id!,
		update: { $set: updateSet },
	});
	if (res.outcome) {
		// The same save, expecting what the server reported, offered through the
		// conflict dialog. Without it the only way past a refusal is reloading,
		// which throws away the edits the operator is standing in front of.
		if ((res as T.API.MenuConflictResponse).conflict === 'menu_version') {
			const refused = res as T.API.MenuConflictResponse;
			store.menu_conflict.offerOverwrite(async () => {
				const retry = await store.api.restaurant_update({
					_id: r._id!,
					update: { $set: updateSet },
					expected_menu_version: refused.menu_version,
				});
				if (retry.outcome) throw retry;
				store.updateRestaurant(localUpdate);
			});
		}
		throw res;
	}
	store.updateRestaurant(localUpdate);
}

function applyDishUpdatesToMenus(menus: RestaurantMenu[], items: DishPatchItem[]): void {
	for (const item of items) {
		const menu = menus.find((entry) => entry._id === item.menu_id);
		const category = menu?.categories.find((entry) => entry._id === item.category_id);
		const dish = category?.dishes.find((entry) => entry._id === item.id);
		if (!dish) continue;
		const { menu_id, category_id, id, ...data } = item;
		Object.assign(dish, data);
	}
}

function buildDishOptionSetAssignmentUpdates(
	menus: RestaurantMenu[],
	optionSetId: string,
	addToDishIds: string[]
): DishPatchItem[] {
	const addTo = new Set(addToDishIds);
	const itemsToUpdate: DishPatchItem[] = [];
	for (const menu of menus) {
		for (const category of menu.categories || []) {
			for (const dish of category.dishes || []) {
				const optionSets = [...(dish.option_sets || [])];
				const hasOptionSet = optionSets.includes(optionSetId);
				const shouldHave = addTo.has(dish._id);
				if (hasOptionSet === shouldHave) continue;
				const nextOptionSets = shouldHave
					? [...optionSets, optionSetId]
					: optionSets.filter((entry) => entry !== optionSetId);
				itemsToUpdate.push({
					menu_id: menu._id,
					category_id: category._id,
					id: dish._id,
					option_sets: nextOptionSets,
				});
			}
		}
	}
	return itemsToUpdate;
}

function buildDishTagAssignmentUpdates(
	menus: RestaurantMenu[],
	tagId: string,
	addToDishIds: string[]
): DishPatchItem[] {
	const addTo = new Set(addToDishIds);
	const itemsToUpdate: DishPatchItem[] = [];
	for (const menu of menus) {
		for (const category of menu.categories || []) {
			for (const dish of category.dishes || []) {
				const tags = [...(dish.tags || [])];
				const hasTag = tags.includes(tagId);
				const shouldHave = addTo.has(dish._id);
				if (hasTag === shouldHave) continue;
				const nextTags = shouldHave
					? [...tags, tagId]
					: tags.filter((entry) => entry !== tagId);
				itemsToUpdate.push({
					menu_id: menu._id,
					category_id: category._id,
					id: dish._id,
					tags: nextTags,
				});
			}
		}
	}
	return itemsToUpdate;
}

function cloneCategoryWithNewIds(
	sourceCategory: RestaurantCategory,
	menuId: string,
	opts?: { rename?: boolean }
): RestaurantCategory {
	const copy = cloneDeep(sourceCategory);
	const newCategoryId = shortid.generate();
	copy._id = newCategoryId;
	copy.menu_id = menuId;
	if (opts?.rename) {
		copy.name = `${copy.name} - Copy`;
	}
	copy.dishes = (copy.dishes || []).map((dish) => {
		const dishCopy = cloneDeep(dish);
		dishCopy._id = shortid.generate();
		dishCopy.menu_id = menuId;
		dishCopy.category_id = newCategoryId;
		return dishCopy;
	});
	return copy;
}

function menuPayloadWithoutNested(menu: RestaurantMenu): Record<string, unknown> {
	const copy = cloneDeep(menu) as unknown as Record<string, unknown>;
	delete copy.categories;
	return copy;
}

function categoryPayloadWithoutDishes(category: RestaurantCategory): Record<string, unknown> {
	const copy = cloneDeep(category) as unknown as Record<string, unknown>;
	delete copy.dishes;
	return copy;
}

function modifierPayloadWithoutOptions(
	modifier: RestaurantOptionSet
): Record<string, unknown> {
	const copy = cloneDeep(modifier) as unknown as Record<string, unknown>;
	delete copy.options;
	return copy;
}

async function patchOriginalItem(
	store: RootStore,
	restaurantId: string,
	ref: DishRefOriginal,
	data: Record<string, unknown>
): Promise<void> {
	if (usesLegacyMenuPersistence(store)) {
		const menus = cloneDeep(store.restaurant!.menus);
		applyDishUpdatesToMenus(menus, [{ ...ref, ...data } as DishPatchItem]);
		await persistRestaurantDoc(store, { menus }, { menus });
		return;
	}
	const res = await store.api.menu_partial_update_item({
		restaurant_id: restaurantId,
		menu_id: ref.menu_id,
		category_id: ref.category_id,
		item_id: ref.id,
		data,
	});
	if (res?.error) {
		throw res;
	}
}

async function patchOriginalItems(
	store: RootStore,
	restaurantId: string,
	items: DishPatchItem[]
): Promise<void> {
	if (items.length === 0) return;
	if (usesLegacyMenuPersistence(store)) {
		const menus = cloneDeep(store.restaurant!.menus);
		applyDishUpdatesToMenus(menus, items);
		await persistRestaurantDoc(store, { menus }, { menus });
		return;
	}
	for (const item of items) {
		const { menu_id, category_id, id, ...data } = item;
		await patchOriginalItem(store, restaurantId, { menu_id, category_id, id }, data);
	}
}

async function createCategoryWithDishes(
	store: RootStore,
	restaurantId: string,
	menuId: string,
	sourceCategory: RestaurantCategory,
	opts?: { rename?: boolean }
): Promise<void> {
	const copy = cloneDeep(sourceCategory);
	const newCategoryId = shortid.generate();
	copy._id = newCategoryId;
	copy.menu_id = menuId;
	if (opts?.rename) {
		copy.name = `${copy.name} - Copy`;
	}
	const dishes = (copy.dishes || []).map((dish) => {
		const dishCopy = cloneDeep(dish);
		dishCopy._id = shortid.generate();
		dishCopy.menu_id = menuId;
		dishCopy.category_id = newCategoryId;
		return dishCopy;
	});
	const categoryPayload = categoryPayloadWithoutDishes(copy);
	const res = await store.api.menu_create_category({
		restaurant_id: restaurantId,
		menu_id: menuId,
		data: { ...categoryPayload, dishes },
	});
	if (res?.error) {
		throw res;
	}
}

async function moveOriginalCategoryToMenu(
	store: RootStore,
	restaurantId: string,
	fromMenuId: string,
	toMenuId: string,
	category: RestaurantCategory,
	sortOrder: number
): Promise<void> {
	const copy = cloneDeep(category);
	copy.menu_id = toMenuId;
	const dishes = (copy.dishes || []).map((dish) => ({
		...cloneDeep(dish),
		menu_id: toMenuId,
		category_id: copy._id,
	}));
	const categoryPayload = categoryPayloadWithoutDishes(copy);
	const createRes = await store.api.menu_create_category({
		restaurant_id: restaurantId,
		menu_id: toMenuId,
		data: { ...categoryPayload, sort_order: sortOrder, dishes },
	});
	if (createRes?.error) {
		throw createRes;
	}
	const deleteRes = await store.api.menu_delete_category({
		restaurant_id: restaurantId,
		menu_id: fromMenuId,
		category_id: category._id,
	});
	if (deleteRes?.error) {
		throw deleteRes;
	}
}

async function moveOriginalDishToCategory(
	store: RootStore,
	restaurantId: string,
	from: DishRefOriginal,
	toMenuId: string,
	toCategoryId: string,
	dish: RestaurantDish,
	sortOrder: number
): Promise<void> {
	const copy = cloneDeep(dish);
	copy.menu_id = toMenuId;
	copy.category_id = toCategoryId;
	// @ts-ignore
	copy.sort_order = sortOrder as unknown as number;
	const createRes = await store.api.menu_create_item({
		restaurant_id: restaurantId,
		menu_id: toMenuId,
		category_id: toCategoryId,
		data: copy,
	});
	if (createRes?.error) {
		throw createRes;
	}
	const deleteRes = await store.api.menu_delete_item({
		restaurant_id: restaurantId,
		menu_id: from.menu_id,
		category_id: from.category_id,
		item_id: from.id,
	});
	if (deleteRes?.error) {
		throw deleteRes;
	}
}

export async function refreshOriginalMenusFromApi(
	store: RootStore,
	restaurantId: string
): Promise<void> {
	if (usesLegacyMenuPersistence(store)) {
		return;
	}
	const currentDisabledById = new Map(
		(store.restaurant?.option_sets || []).map((optionSet) => [optionSet._id, optionSet.disabled])
	);
	const got = await store.api.menu_get_menus(restaurantId);
	const update: Partial<RestaurantSchema> = {};
	if (Array.isArray(got?.menus)) {
		update.menus = got.menus;
	}
	if (Array.isArray(got?.option_sets)) {
		update.option_sets = got.option_sets.map((optionSet: RestaurantOptionSet) => ({
			...optionSet,
			...(currentDisabledById.get(optionSet._id) !== undefined
				? { disabled: currentDisabledById.get(optionSet._id) }
				: {}),
		}));
	}
	if (Array.isArray(got?.dish_tags)) {
		update.dish_tags = got.dish_tags;
	} else if (Array.isArray(got?.tags)) {
		update.dish_tags = mapTagsFromApi(got.tags);
	}
	if (Object.keys(update).length > 0) {
		store.updateRestaurant(update);
	}
}

/** Whether two conditions objects declare different sales channels. */
function channelsDiffer(before: any, after: any): boolean {
	return (["pos", "online"] as const).some(
		(field) =>
			hasOwnChannel(before, field) !== hasOwnChannel(after, field) ||
			JSON.stringify(before?.[field] ?? null) !== JSON.stringify(after?.[field] ?? null)
	);
}

/** Sends an entity's conditions through menu-api so cascader.serviceGroup runs.
 *
 * The legacy ("original") schema writes the restaurant document directly and never reaches
 * PatchMenu, so nothing cascaded the channels to categories and items. Routing the flag through
 * menu-api keeps the cascade rule in one place; everything else here still goes out as the
 * whole-document $set it always did.
 *
 * The *whole* conditions object is sent, not just the channel: menu-api flattens a patch to
 * dotted paths and writes non-pointer fields unconditionally, so a partial object would blank
 * out the siblings it omits — age_restricted and the pre_order block among them.
 */
async function cascadeChannelsViaMenuApi(
	store: RootStore,
	restaurantId: string,
	ids: { menu_id: string; category_id?: string },
	conditions: any
): Promise<void> {
	try {
		const res = ids.category_id
			? await store.api.menu_partial_update_category({
					restaurant_id: restaurantId,
					menu_id: ids.menu_id,
					category_id: ids.category_id,
					data: { conditions },
			  })
			: await store.api.menu_partial_update_menu({
					restaurant_id: restaurantId,
					menu_id: ids.menu_id,
					data: { conditions },
			  });
		if (res?.error) throw res;
	} catch (e) {
		// The save already succeeded, so this must not fail it. Children keep inheriting at read
		// time until the next save — degraded, not broken.
		logger.captureException(
			e instanceof Error ? e : new Error("failed to cascade sales channels")
		);
	}
}

export async function saveOriginalMenu(
	store: RootStore,
	values: RestaurantMenu,
	isCreate: boolean
): Promise<void> {
	const r = store.restaurant!;
	const copyValues = cloneDeep(values);
	if (usesLegacyMenuPersistence(store)) {
		const menus = cloneDeep(r.menus);
		if (isCreate) {
			menus.push(copyValues);
		} else {
			const menuIndex = menus.findIndex((menu) => menu._id === copyValues._id);
			if (menuIndex === -1) {
				throw new Error('Menu not found');
			}
			const existing = menus[menuIndex];
			menus[menuIndex] = {
				...existing,
				...(menuPayloadWithoutNested(copyValues) as Partial<RestaurantMenu>),
				categories: existing.categories,
			};
		}
		const previousMenu = r.menus.find((menu) => menu._id === copyValues._id);
		await persistRestaurantDoc(store, { menus }, { menus });
		// After the document write, or the whole-array $set would undo the cascade.
		if (!isCreate && channelsDiffer(previousMenu?.conditions, copyValues.conditions)) {
			await cascadeChannelsViaMenuApi(store, r._id!, { menu_id: copyValues._id }, copyValues.conditions);
		}
		return;
	}
	const res = isCreate
		? await store.api.menu_create_menu({
				restaurant_id: r._id!,
				menu: copyValues,
		  })
		: await store.api.menu_partial_update_menu({
				restaurant_id: r._id!,
				menu_id: copyValues._id,
				data: menuPayloadWithoutNested(copyValues),
		  });
	if (res?.error) {
		throw res;
	}
	await refreshOriginalMenusFromApi(store, r._id!);
}

export async function saveOriginalCategory(
	store: RootStore,
	menuId: string,
	values: RestaurantCategory,
	isCreate: boolean
): Promise<void> {
	const r = store.restaurant!;
	const copyValues = cloneDeep(values);
	if (usesLegacyMenuPersistence(store)) {
		const menus = cloneDeep(r.menus);
		const menuIndex = menus.findIndex((menu) => menu._id === menuId);
		if (menuIndex === -1) {
			throw new Error('Menu not found');
		}
		if (isCreate) {
			menus[menuIndex].categories.push(copyValues);
		} else {
			const categoryIndex = menus[menuIndex].categories.findIndex(
				(category) => category._id === copyValues._id
			);
			if (categoryIndex === -1) {
				throw new Error('Category not found');
			}
			const existing = menus[menuIndex].categories[categoryIndex];
			menus[menuIndex].categories[categoryIndex] = {
				...existing,
				...(categoryPayloadWithoutDishes(copyValues) as Partial<RestaurantCategory>),
				dishes: existing.dishes,
			};
		}
		const previousCategory = r.menus
			.find((menu) => menu._id === menuId)
			?.categories.find((category) => category._id === copyValues._id);
		await persistRestaurantDoc(store, { menus }, { menus });
		// One level down: a category's channels reach its dishes.
		if (!isCreate && channelsDiffer(previousCategory?.conditions, copyValues.conditions)) {
			await cascadeChannelsViaMenuApi(
				store,
				r._id!,
				{ menu_id: menuId, category_id: copyValues._id },
				copyValues.conditions
			);
		}
		return;
	}
	const res = isCreate
		? await store.api.menu_create_category({
				restaurant_id: r._id!,
				menu_id: menuId,
				data: copyValues,
		  })
		: await store.api.menu_partial_update_category({
				restaurant_id: r._id!,
				menu_id: menuId,
				category_id: copyValues._id,
				data: categoryPayloadWithoutDishes(copyValues),
		  });
	if (res?.error) {
		throw res;
	}
	await refreshOriginalMenusFromApi(store, r._id!);
}

async function saveOriginalDishViaRestaurant(
	store: RootStore,
	menuId: string,
	categoryId: string,
	values: RestaurantDish,
	taxRates?: T.Schema.Restaurant.RestaurantSchema['settings']['region']['tax']['rates']
): Promise<void> {
	const r = store.restaurant!;
	const menus = cloneDeep(r.menus);
	const menuIndex = menus.findIndex((menu) => menu._id === menuId);
	if (menuIndex === -1) {
		throw new Error('Menu not found');
	}
	const categoryIndex = menus[menuIndex].categories.findIndex(
		(category) => category._id === categoryId
	);
	if (categoryIndex === -1) {
		throw new Error('Category not found');
	}

	const copyValues = cloneDeep(values);
	const dishes = menus[menuIndex].categories[categoryIndex].dishes || [];
	const existing = dishes.findIndex((dish) => dish._id === copyValues._id);
	if (existing === -1) {
		menus[menuIndex].categories[categoryIndex].dishes = [...dishes, copyValues];
	} else {
		menus[menuIndex].categories[categoryIndex].dishes[existing] = copyValues;
	}

	const updateSet: Record<string, unknown> = { menus };
	if (taxRates) {
		updateSet['settings.region.tax.rates'] = taxRates;
	}

	const restaurantUpdate: Partial<RestaurantSchema> = { menus };
	if (taxRates) {
		restaurantUpdate.settings = {
			...r.settings,
			region: {
				...r.settings.region,
				tax: {
					...r.settings.region.tax,
					rates: taxRates,
				},
			},
		};
	}
	await persistRestaurantDoc(store, updateSet, restaurantUpdate);
}

export async function saveOriginalDish(
	store: RootStore,
	menuId: string,
	categoryId: string,
	values: RestaurantDish,
	isCreate: boolean,
	taxRates?: T.Schema.Restaurant.RestaurantSchema['settings']['region']['tax']['rates']
): Promise<void> {
	if (usesLegacyMenuPersistence(store)) {
		await saveOriginalDishViaRestaurant(store, menuId, categoryId, values, taxRates);
		return;
	}

	const r = store.restaurant!;
	const copyValues = cloneDeep(values);
	const res = isCreate
		? await store.api.menu_create_item({
				restaurant_id: r._id!,
				menu_id: menuId,
				category_id: categoryId,
				data: copyValues,
		  })
		: await store.api.menu_partial_update_item({
				restaurant_id: r._id!,
				menu_id: menuId,
				category_id: categoryId,
				item_id: copyValues._id,
				data: copyValues,
		  });
	if (res?.error) {
		throw res;
	}
	if (taxRates) {
		const taxRes = await store.api.restaurant_update({
			_id: r._id!,
			update: { $set: { 'settings.region.tax.rates': taxRates } },
		});
		if (taxRes.outcome) {
			throw taxRes;
		}
		store.updateRestaurant({
			settings: {
				...r.settings,
				region: {
					...r.settings.region,
					tax: {
						...r.settings.region.tax,
						rates: taxRates,
					},
				},
			},
		});
	}
	await refreshOriginalMenusFromApi(store, r._id!);
}

export function collectDishRefsFromMenus(
	menus: RestaurantMenu[],
	selectedDishIds: string[]
): DishRefOriginal[] {
	const selected = new Set(selectedDishIds);
	const refs: DishRefOriginal[] = [];
	for (const menu of menus) {
		for (const category of menu.categories || []) {
			for (const dish of category.dishes || []) {
				if (selected.has(dish._id)) {
					refs.push({
						menu_id: menu._id,
						category_id: category._id,
						id: dish._id,
					});
				}
			}
		}
	}
	return refs;
}

export async function batchUpdateOriginalDishStatus(
	store: RootStore,
	menus: RestaurantMenu[],
	selectedDishIds: string[],
	status: string | null
): Promise<void> {
	const r = store.restaurant!;
	const refs = collectDishRefsFromMenus(menus, selectedDishIds);
	if (refs.length === 0) return;
	await patchOriginalItems(
		store,
		r._id!,
		refs.map((ref) => ({
			...ref,
			status: status as RestaurantDish['status'],
		}))
	);
	await refreshOriginalMenusFromApi(store, r._id!);
}

export async function copyOriginalMenuNode(
	store: RootStore,
	type: 'menu' | 'category' | 'item',
	menuId: string,
	categoryId?: string,
	itemId?: string
): Promise<void> {
	const r = store.restaurant!;
	if (usesLegacyMenuPersistence(store)) {
		const menus = cloneDeep(r.menus);
		if (type === 'menu') {
			const source = menus.find((menu) => menu._id === menuId);
			if (!source) {
				throw new Error('Menu not found');
			}
			const copy = cloneDeep(source);
			const newMenuId = shortid.generate();
			copy._id = newMenuId;
			copy.name = `${copy.name} - Copy`;
			copy.categories = (source.categories || []).map((category) =>
				cloneCategoryWithNewIds(category, newMenuId)
			);
			menus.push(copy);
		} else if (type === 'category') {
			const menuIndex = menus.findIndex((menu) => menu._id === menuId);
			const category = menus[menuIndex]?.categories.find((entry) => entry._id === categoryId);
			if (menuIndex === -1 || !category) {
				throw new Error('Category not found');
			}
			menus[menuIndex].categories.push(
				cloneCategoryWithNewIds(category, menuId, { rename: true })
			);
		} else if (type === 'item') {
			const menu = menus.find((entry) => entry._id === menuId);
			const category = menu?.categories.find((entry) => entry._id === categoryId);
			const dish = category?.dishes.find((entry) => entry._id === itemId);
			if (!menu || !category || !dish) {
				throw new Error('Item not found');
			}
			const copy = cloneDeep(dish);
			copy._id = shortid.generate();
			copy.name = `${copy.name} - Copy`;
			category.dishes.push(copy);
		}
		await persistRestaurantDoc(store, { menus }, { menus });
		return;
	}
	if (type === 'menu') {
		const source = r.menus.find((menu) => menu._id === menuId);
		if (!source) {
			throw new Error('Menu not found');
		}
		const copy = cloneDeep(source);
		const newMenuId = shortid.generate();
		copy._id = newMenuId;
		copy.name = `${copy.name} - Copy`;
		const menuPayload = menuPayloadWithoutNested(copy);
		const createMenuRes = await store.api.menu_create_menu({
			restaurant_id: r._id!,
			menu: { ...menuPayload, _id: newMenuId } as RestaurantMenu,
		});
		if (createMenuRes?.error) {
			throw createMenuRes;
		}
		for (const category of source.categories || []) {
			await createCategoryWithDishes(store, r._id!, newMenuId, category);
		}
	} else if (type === 'category') {
		const menu = r.menus.find((m) => m._id === menuId);
		const category = menu?.categories.find((c) => c._id === categoryId);
		if (!menu || !category) {
			throw new Error('Category not found');
		}
		await createCategoryWithDishes(store, r._id!, menuId, category, { rename: true });
	} else if (type === 'item') {
		const menu = r.menus.find((m) => m._id === menuId);
		const category = menu?.categories.find((c) => c._id === categoryId);
		const dish = category?.dishes.find((d) => d._id === itemId);
		if (!menu || !category || !dish) {
			throw new Error('Item not found');
		}
		const copy = cloneDeep(dish);
		copy._id = shortid.generate();
		copy.name = `${copy.name} - Copy`;
		const createRes = await store.api.menu_create_item({
			restaurant_id: r._id!,
			menu_id: menuId,
			category_id: categoryId!,
			data: copy,
		});
		if (createRes?.error) {
			throw createRes;
		}
	}
	await refreshOriginalMenusFromApi(store, r._id!);
}

export async function saveOriginalMenuReorder(
	store: RootStore,
	currentMenus: RestaurantMenu[],
	backupMenus: RestaurantMenu[]
): Promise<void> {
	const r = store.restaurant!;
	if (usesLegacyMenuPersistence(store)) {
		const menus = cloneDeep(currentMenus);
		await persistRestaurantDoc(store, { menus }, { menus });
		return;
	}
	const arraysEqual = (a: string[], b: string[]) =>
		a.length === b.length && a.every((v, i) => v === b[i]);

	const backupDishById = new Map<string, RestaurantDish>();
	const backupCategoryToMenu = new Map<string, string>();
	for (const menu of backupMenus) {
		for (const category of menu.categories || []) {
			backupCategoryToMenu.set(category._id, menu._id);
			for (const dish of category.dishes || []) {
				backupDishById.set(dish._id, dish);
			}
		}
	}

	const categoriesToMove: Array<{
		menu_id: string;
		id: string;
		data: { menu_id: string };
	}> = [];
	for (const menu of currentMenus) {
		for (const category of menu.categories || []) {
			const prevMenuId = backupCategoryToMenu.get(category._id);
			if (prevMenuId && prevMenuId !== menu._id) {
				categoriesToMove.push({
					menu_id: prevMenuId,
					id: category._id,
					data: { menu_id: menu._id },
				});
			}
		}
	}

	const dishesToMove: Array<{
		menu_id: string;
		category_id: string;
		id: string;
		data: { menu_id: string; category_id: string };
	}> = [];
	for (const menu of currentMenus) {
		for (const category of menu.categories || []) {
			for (const dish of category.dishes || []) {
				const prev = backupDishById.get(dish._id);
				if (!prev) continue;
				if (prev.category_id !== dish.category_id || prev.menu_id !== menu._id) {
					dishesToMove.push({
						menu_id: prev.menu_id,
						category_id: prev.category_id,
						id: dish._id,
						data: { menu_id: menu._id, category_id: dish.category_id },
					});
				}
			}
		}
	}

	const currentMenuOrder = currentMenus.map((m) => m._id);
	const backupMenuOrder = backupMenus.map((m) => m._id);
	const menuOrderChanged = !arraysEqual(currentMenuOrder, backupMenuOrder);

	const categoryOrderTasks: Array<{ menu_id: string; order: string[] }> = [];
	for (const menu of currentMenus) {
		const currentCatOrder = (menu.categories || []).map((c) => c._id);
		const backupMenu = backupMenus.find((m) => m._id === menu._id);
		const backupCatOrder = backupMenu
			? (backupMenu.categories || []).map((c) => c._id)
			: [];
		if (!arraysEqual(currentCatOrder, backupCatOrder)) {
			categoryOrderTasks.push({ menu_id: menu._id, order: currentCatOrder });
		}
	}

	const dishOrderTasks: Array<{
		menu_id: string;
		category_id: string;
		order: string[];
	}> = [];
	for (const menu of currentMenus) {
		const backupMenu = backupMenus.find((m) => m._id === menu._id);
		for (const category of menu.categories || []) {
			const currentDishOrder = (category.dishes || []).map((d) => d._id);
			const backupCategory = backupMenu?.categories?.find((c) => c._id === category._id);
			const backupDishOrder = backupCategory
				? (backupCategory.dishes || []).map((d) => d._id)
				: [];
			if (!arraysEqual(currentDishOrder, backupDishOrder)) {
				dishOrderTasks.push({
					menu_id: menu._id,
					category_id: category._id,
					order: currentDishOrder,
				});
			}
		}
	}

	if (categoriesToMove.length > 0) {
		for (const move of categoriesToMove) {
			const sourceMenu = currentMenus.find((m) => m._id === move.menu_id);
			const category =
				sourceMenu?.categories.find((c) => c._id === move.id) ??
				currentMenus
					.find((m) => m._id === move.data.menu_id)
					?.categories.find((c) => c._id === move.id);
			if (!category) continue;
			const targetMenu = currentMenus.find((m) => m._id === move.data.menu_id);
			const sortOrder = (targetMenu?.categories || []).findIndex((c) => c._id === move.id);
			await moveOriginalCategoryToMenu(
				store,
				r._id!,
				move.menu_id,
				move.data.menu_id,
				category,
				sortOrder >= 0 ? sortOrder : 0
			);
		}
	}
	if (dishesToMove.length > 0) {
		for (const move of dishesToMove) {
			const prevDish = backupDishById.get(move.id);
			if (!prevDish) continue;
			const targetMenu = currentMenus.find((m) => m._id === move.data.menu_id);
			const targetCategory = targetMenu?.categories.find(
				(c) => c._id === move.data.category_id
			);
			const sortOrder = (targetCategory?.dishes || []).findIndex((d) => d._id === move.id);
			await moveOriginalDishToCategory(
				store,
				r._id!,
				{
					menu_id: move.menu_id,
					category_id: move.category_id,
					id: move.id,
				},
				move.data.menu_id,
				move.data.category_id,
				prevDish,
				sortOrder >= 0 ? sortOrder : 0
			);
		}
	}
	if (menuOrderChanged) {
		for (let i = 0; i < currentMenuOrder.length; i++) {
			const res = await store.api.menu_partial_update_menu({
				restaurant_id: r._id!,
				menu_id: currentMenuOrder[i],
				data: { sort_order: i },
			});
			if (res?.error) throw res;
		}
	}
	for (const task of categoryOrderTasks) {
		for (let i = 0; i < task.order.length; i++) {
			const res = await store.api.menu_partial_update_category({
				restaurant_id: r._id!,
				menu_id: task.menu_id,
				category_id: task.order[i],
				data: { sort_order: i },
			});
			if (res?.error) throw res;
		}
	}
	for (const task of dishOrderTasks) {
		for (let i = 0; i < task.order.length; i++) {
			const res = await store.api.menu_partial_update_item({
				restaurant_id: r._id!,
				menu_id: task.menu_id,
				category_id: task.category_id,
				item_id: task.order[i],
				data: { sort_order: i },
			});
			if (res?.error) throw res;
		}
	}
	await refreshOriginalMenusFromApi(store, r._id!);
}

export async function saveOriginalMenuPrices(
	store: RootStore,
	currentMenus: RestaurantMenu[],
	backupMenus: RestaurantMenu[]
): Promise<void> {
	const r = store.restaurant!;
	if (usesLegacyMenuPersistence(store)) {
		const menus = cloneDeep(currentMenus);
		await persistRestaurantDoc(store, { menus }, { menus });
		return;
	}
	const backupPriceByDishId = new Map<string, number>();
	for (const menu of backupMenus) {
		for (const category of menu.categories || []) {
			for (const dish of category.dishes || []) {
				backupPriceByDishId.set(dish._id, dish.price);
			}
		}
	}

	const updatedItems: Array<{
		id: string;
		menu_id: string;
		category_id: string;
		price: number;
	}> = [];
	for (const menu of currentMenus) {
		for (const category of menu.categories || []) {
			for (const dish of category.dishes || []) {
				const prevPrice = backupPriceByDishId.get(dish._id);
				if (prevPrice !== undefined && prevPrice !== dish.price) {
					updatedItems.push({
						id: dish._id,
						menu_id: menu._id,
						category_id: category._id,
						price: dish.price,
					});
				}
			}
		}
	}
	if (updatedItems.length === 0) return;
	await patchOriginalItems(store, r._id!, updatedItems);
	await refreshOriginalMenusFromApi(store, r._id!);
}

export async function syncOriginalDishOptionSetAssignments(
	store: RootStore,
	optionSetId: string,
	addToDishIds: string[]
): Promise<void> {
	const r = store.restaurant!;
	const itemsToUpdate = buildDishOptionSetAssignmentUpdates(
		r.menus || [],
		optionSetId,
		addToDishIds
	);
	if (itemsToUpdate.length === 0) return;
	await patchOriginalItems(store, r._id!, itemsToUpdate);
}

export async function saveOriginalOptionSet(
	store: RootStore,
	values: RestaurantOptionSet,
	isCreate: boolean,
	addToDishIds: string[],
	oldOptionSet: RestaurantOptionSet | null
): Promise<string> {
	const r = store.restaurant!;
	const copyValues = cloneDeep(values);
	if (copyValues.using_points === true) {
		copyValues.conditions.multi_select = true;
	}

	let optionSetId = copyValues._id;
	if (usesLegacyMenuPersistence(store)) {
		const optionSets = cloneDeep(r.option_sets || []);
		if (isCreate) {
			optionSets.push(copyValues);
		} else {
			const optionSetIndex = optionSets.findIndex((entry) => entry._id === copyValues._id);
			if (optionSetIndex === -1) {
				throw new Error('Option set not found');
			}
			optionSets[optionSetIndex] = copyValues;
		}
		const menus = cloneDeep(r.menus || []);
		const itemsToUpdate = buildDishOptionSetAssignmentUpdates(
			menus,
			optionSetId,
			addToDishIds
		);
		applyDishUpdatesToMenus(menus, itemsToUpdate);
		await persistRestaurantDoc(
			store,
			{ menus, option_sets: optionSets },
			{ menus, option_sets: optionSets }
		);
		return optionSetId;
	}
	if (isCreate) {
		const res = await store.api.menu_create_modifier({
			restaurant_id: r._id!,
			data: copyValues,
		});
		if (res?.error) throw res;
		optionSetId =
			(res as { modifier?: { _id?: string; id?: string }; id?: string })?.modifier?._id ??
			(res as { modifier?: { id?: string } })?.modifier?.id ??
			(res as { id?: string })?.id ??
			copyValues._id;
	} else {
		const res = await store.api.menu_partial_update_modifier({
			restaurant_id: r._id!,
			modifier_id: copyValues._id,
			data: modifierPayloadWithoutOptions(copyValues),
		});
		if (res?.error) throw res;

		if (oldOptionSet) {
			const deletePromises: Promise<any>[] = [];
			for (const oldOption of oldOptionSet.options || []) {
				const stillExists = (copyValues.options || []).some((o) => o._id === oldOption._id);
				if (!stillExists && oldOption._id) {
					deletePromises.push(
						store.api.menu_delete_modifier_option({
							restaurant_id: r._id!,
							modifier_id: copyValues._id,
							option_id: oldOption._id,
						})
					);
				}
			}
			await Promise.all(deletePromises);
		}
		const options = copyValues.options || [];
		if (options.length > 0) {
			const optionRes = await store.api.menu_batch_upsert_modifier_options({
				restaurant_id: r._id!,
				modifier_id: copyValues._id,
				options,
			});
			if (optionRes?.error) throw optionRes;
			const failedResult = (optionRes?.results || []).find(
				(result: { error?: string }) => result?.error
			);
			if (failedResult) {
				throw { error: failedResult.error || 'Failed to update modifier options' };
			}
		}
	}

	await syncOriginalDishOptionSetAssignments(store, optionSetId, addToDishIds);
	await refreshOriginalMenusFromApi(store, r._id!);
	return optionSetId;
}

export async function deleteOriginalOptionSet(
	store: RootStore,
	optionSetId: string
): Promise<void> {
	const r = store.restaurant!;
	if (usesLegacyMenuPersistence(store)) {
		const optionSets = (r.option_sets || []).filter((entry) => entry._id !== optionSetId);
		const menus = cloneDeep(r.menus || []);
		const itemsToUpdate = buildOptionSetDetachAllUpdates(menus, [optionSetId]);
		applyDishUpdatesToMenus(menus, itemsToUpdate);
		await persistRestaurantDoc(
			store,
			{ menus, option_sets: optionSets },
			{ menus, option_sets: optionSets }
		);
		return;
	}
	const res = await store.api.menu_delete_modifier({
		restaurant_id: r._id!,
		modifier_id: optionSetId,
	});
	if (res?.error) throw res;
	await refreshOriginalMenusFromApi(store, r._id!);
}

export async function bulkUpdateOriginalOptionSets(
	store: RootStore,
	optionSetIds: string[],
	patch: Partial<Pick<RestaurantOptionSet, 'disabled'>>
): Promise<void> {
	const r = store.restaurant!;
	const selected = new Set(optionSetIds);
	if (usesLegacyMenuPersistence(store)) {
		const optionSets = (r.option_sets || []).map((optionSet) =>
			selected.has(optionSet._id) ? { ...optionSet, ...patch } : optionSet
		);
		await persistRestaurantDoc(store, { option_sets: optionSets }, { option_sets: optionSets });
		return;
	}
	for (const optionSetId of optionSetIds) {
		const res = await store.api.menu_partial_update_modifier({
			restaurant_id: r._id!,
			modifier_id: optionSetId,
			data: patch,
		});
		if (res?.error) throw res;
	}
	if (patch.disabled !== undefined) {
		const optionSets = (r.option_sets || []).map((optionSet) =>
			selected.has(optionSet._id)
				? { ...optionSet, disabled: patch.disabled }
				: optionSet
		);
		const res = await store.api.restaurant_update({
			_id: r._id!,
			update: { $set: { option_sets: optionSets } },
		});
		if (res?.outcome) throw res;
		store.updateRestaurant({ option_sets: optionSets });
	}
	await refreshOriginalMenusFromApi(store, r._id!);
}

export async function bulkDeleteOriginalOptionSets(
	store: RootStore,
	optionSetIds: string[]
): Promise<void> {
	const r = store.restaurant!;
	if (usesLegacyMenuPersistence(store)) {
		const selected = new Set(optionSetIds);
		const optionSets = (r.option_sets || []).filter((entry) => !selected.has(entry._id));
		const menus = cloneDeep(r.menus || []);
		const itemsToUpdate = buildOptionSetDetachAllUpdates(menus, optionSetIds);
		applyDishUpdatesToMenus(menus, itemsToUpdate);
		await persistRestaurantDoc(
			store,
			{ menus, option_sets: optionSets },
			{ menus, option_sets: optionSets }
		);
		return;
	}
	for (const optionSetId of optionSetIds) {
		const res = await store.api.menu_delete_modifier({
			restaurant_id: r._id!,
			modifier_id: optionSetId,
		});
		if (res?.error) throw res;
	}
	await refreshOriginalMenusFromApi(store, r._id!);
}

export async function bulkAttachOriginalOptionSetsToDishes(
	store: RootStore,
	optionSetIds: string[],
	dishIds: string[]
): Promise<void> {
	const r = store.restaurant!;
	const itemsToUpdate = buildOptionSetAttachUpdates(r.menus || [], optionSetIds, dishIds);
	if (itemsToUpdate.length > 0) {
		await patchOriginalItems(store, r._id!, itemsToUpdate);
	}
	await refreshOriginalMenusFromApi(store, r._id!);
}

export async function bulkDetachOriginalOptionSetsFromDishes(
	store: RootStore,
	optionSetIds: string[],
	dishIds: string[]
): Promise<void> {
	const r = store.restaurant!;
	const itemsToUpdate = buildOptionSetDetachUpdates(r.menus || [], optionSetIds, dishIds);
	if (itemsToUpdate.length > 0) {
		await patchOriginalItems(store, r._id!, itemsToUpdate);
	}
	await refreshOriginalMenusFromApi(store, r._id!);
}

export async function copyOriginalOptionSet(
	store: RootStore,
	source: RestaurantOptionSet
): Promise<void> {
	const r = store.restaurant!;
	const copyValues = cloneDeep(source);
	copyValues.name = `${copyValues.name} - Copy`;
	if (usesLegacyMenuPersistence(store)) {
		copyValues._id = shortid.generate();
		for (const option of copyValues.options || []) {
			option._id = shortid.generate();
		}
		const optionSets = [...(r.option_sets || []), copyValues];
		await persistRestaurantDoc(store, { option_sets: optionSets }, { option_sets: optionSets });
		return;
	}
	const res = await store.api.menu_create_modifier({
		restaurant_id: r._id!,
		data: copyValues,
	});
	if (res?.error) throw res;
	await refreshOriginalMenusFromApi(store, r._id!);
}

export async function saveOriginalOptionSetOrder(
	store: RootStore,
	order: string[]
): Promise<void> {
	const r = store.restaurant!;
	if (usesLegacyMenuPersistence(store)) {
		const byId = new Map((r.option_sets || []).map((optionSet) => [optionSet._id, optionSet]));
		const optionSets = order
			.map((optionSetId) => byId.get(optionSetId))
			.filter((optionSet): optionSet is RestaurantOptionSet => !!optionSet);
		await persistRestaurantDoc(store, { option_sets: optionSets }, { option_sets: optionSets });
		return;
	}
	for (let i = 0; i < order.length; i++) {
		const res = await store.api.menu_partial_update_modifier({
			restaurant_id: r._id!,
			modifier_id: order[i],
			data: { sort_order: i },
		});
		if (res?.error) throw res;
	}
	await refreshOriginalMenusFromApi(store, r._id!);
}

export async function syncOriginalDishTagAssignments(
	store: RootStore,
	tagId: string,
	addToDishIds: string[]
): Promise<void> {
	const r = store.restaurant!;
	const itemsToUpdate = buildDishTagAssignmentUpdates(r.menus || [], tagId, addToDishIds);
	if (itemsToUpdate.length === 0) return;
	await patchOriginalItems(store, r._id!, itemsToUpdate);
}

export async function saveOriginalTag(
	store: RootStore,
	values: RestaurantDishTag,
	isCreate: boolean,
	addToDishIds: string[]
): Promise<string> {
	const r = store.restaurant!;
	const payload = {
		name: values.name,
		type: values.type,
		text: values.text,
		icon: values.icon,
		color: values.color,
	};

	let tagId = values._id;
	if (usesLegacyMenuPersistence(store)) {
		const dishTags = cloneDeep(r.dish_tags || []);
		const payload = {
			name: values.name,
			type: values.type,
			text: values.text,
			icon: values.icon,
			color: values.color,
		};
		if (isCreate) {
			dishTags.push({ ...values, ...payload, _id: values._id });
		} else {
			const tagIndex = dishTags.findIndex((entry) => entry._id === values._id);
			if (tagIndex === -1) {
				throw new Error('Tag not found');
			}
			dishTags[tagIndex] = { ...dishTags[tagIndex], ...payload, _id: values._id };
		}
		const menus = cloneDeep(r.menus || []);
		const itemsToUpdate = buildDishTagAssignmentUpdates(menus, tagId, addToDishIds);
		applyDishUpdatesToMenus(menus, itemsToUpdate);
		await persistRestaurantDoc(
			store,
			{ menus, dish_tags: dishTags },
			{ menus, dish_tags: dishTags }
		);
		return tagId;
	}
	if (isCreate) {
		const res = await store.api.tags_create({
			restaurant_id: r._id!,
			data: payload,
		});
		if (res?.error) throw res;
		tagId =
			(res as { tag?: { _id?: string; id?: string }; id?: string })?.tag?._id ??
			(res as { tag?: { id?: string } })?.tag?.id ??
			(res as { id?: string })?.id ??
			values._id;
	} else {
		const res = await store.api.tags_update({
			restaurant_id: r._id!,
			tag_id: values._id,
			data: payload,
		});
		if (res?.error) throw res;
	}

	await syncOriginalDishTagAssignments(store, tagId, addToDishIds);
	await refreshOriginalMenusFromApi(store, r._id!);
	return tagId;
}

export async function deleteOriginalTag(store: RootStore, tagId: string): Promise<void> {
	const r = store.restaurant!;
	if (usesLegacyMenuPersistence(store)) {
		const dishTags = (r.dish_tags || []).filter((entry) => entry._id !== tagId);
		const menus = cloneDeep(r.menus || []);
		const itemsToUpdate: Array<DishRefOriginal & { tags: string[] }> = [];
		for (const menu of menus) {
			for (const category of menu.categories || []) {
				for (const dish of category.dishes || []) {
					if (!(dish.tags || []).includes(tagId)) continue;
					itemsToUpdate.push({
						menu_id: menu._id,
						category_id: category._id,
						id: dish._id,
						tags: (dish.tags || []).filter((entry) => entry !== tagId),
					});
				}
			}
		}
		applyDishUpdatesToMenus(menus, itemsToUpdate);
		await persistRestaurantDoc(
			store,
			{ menus, dish_tags: dishTags },
			{ menus, dish_tags: dishTags }
		);
		return;
	}
	const res = await store.api.tags_delete({
		restaurant_id: r._id!,
		tag_id: tagId,
	});
	if (res?.error) throw res;
	await refreshOriginalMenusFromApi(store, r._id!);
}

export async function copyOriginalTag(
	store: RootStore,
	source: RestaurantDishTag
): Promise<void> {
	const r = store.restaurant!;
	if (usesLegacyMenuPersistence(store)) {
		const dishTags = [
			...(r.dish_tags || []),
			{
				...cloneDeep(source),
				_id: shortid.generate(),
				name: `${source.name} - Copy`,
			},
		];
		await persistRestaurantDoc(store, { dish_tags: dishTags }, { dish_tags: dishTags });
		return;
	}
	const res = await store.api.tags_create({
		restaurant_id: r._id!,
		data: {
			name: `${source.name} - Copy`,
			type: source.type,
			text: source.text,
			icon: source.icon,
			color: source.color,
		},
	});
	if (res?.error) throw res;
	await refreshOriginalMenusFromApi(store, r._id!);
}

export async function saveOriginalTagOrder(store: RootStore, order: string[]): Promise<void> {
	const r = store.restaurant!;
	if (usesLegacyMenuPersistence(store)) {
		const byId = new Map((r.dish_tags || []).map((tag) => [tag._id, tag]));
		const dishTags = order
			.map((tagId) => byId.get(tagId))
			.filter((tag): tag is RestaurantDishTag => !!tag);
		await persistRestaurantDoc(store, { dish_tags: dishTags }, { dish_tags: dishTags });
		return;
	}
	for (let i = 0; i < order.length; i++) {
		const res = await store.api.tags_update({
			restaurant_id: r._id!,
			tag_id: order[i],
			data: { sort_order: i },
		});
		if (res?.error) throw res;
	}
	await refreshOriginalMenusFromApi(store, r._id!);
}
